import { Download, Image as ImageIcon, Video, Copy, ExternalLink, Loader2, Film, CheckCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { triggerDownload, cleanName } from "@/lib/download";
import AdAnalysis from "@/components/site/AdAnalysis";
import CompetitorIntelligence from "@/components/site/CompetitorIntelligence";
import CarouselGallery from "@/components/site/CarouselGallery";

export default function ResultPreview({ ad, loading }) {
  const [copied, setCopied] = useState(false);

  const copyBody = async () => {
    const text = [ad?.title, ad?.body].filter(Boolean).join("\n\n").trim();
    if (!text) {
      toast.error("This ad has no text to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Ad copy copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard blocked — select and copy manually");
    }
  };

  if (loading) {
    return (
      <section
        id="result"
        data-testid="result-loading"
        className="relative py-16 md:py-20 border-t border-zinc-200 dark:border-zinc-800"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="border border-zinc-200 dark:border-zinc-800 bg-white/70 dark:bg-zinc-950/60 aspect-video flex items-center justify-center">
            <div className="flex flex-col items-center gap-4 text-zinc-500 dark:text-zinc-500">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
              <span className="font-mono text-xs uppercase tracking-widest">
                Contacting Meta · Extracting media
              </span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!ad) return null;

  const videoUrl = ad.video_hd_url || ad.video_sd_url;
  const thumb = ad.preview_image_url || ad.image_url;
  const base = cleanName(ad.page_name) + (ad.ad_id ? `_${ad.ad_id}` : "");
  const hasCarousel = Array.isArray(ad.cards) && ad.cards.length > 1;

  return (
    <section
      id="result"
      data-testid="result-section"
      className="relative py-16 md:py-24 border-t border-zinc-200 dark:border-zinc-800"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-yellow-500 mb-2">
              // Ready for download
            </p>
            <h2 className="font-display font-bold text-3xl md:text-5xl text-zinc-900 dark:text-white tracking-tight">
              Your ad, unlocked.
            </h2>
          </div>
          <a
            href={ad.source_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-source-link"
            className="hidden md:inline-flex items-center gap-2 text-sm text-zinc-400 dark:text-zinc-600 dark:text-zinc-400 hover:text-yellow-500 transition-colors"
          >
            View original <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {hasCarousel && <CarouselGallery cards={ad.cards} base={base} />}
          {/* Video / image preview — hidden for carousel ads (cards shown above) */}
          {!hasCarousel && (
          <div className={`col-span-12 lg:col-span-8 border border-zinc-200 dark:border-zinc-800 bg-black overflow-hidden`}>
            {videoUrl ? (
              <video
                data-testid="result-video"
                key={videoUrl}
                controls
                poster={thumb || undefined}
                className="w-full aspect-video bg-black"
              >
                <source src={videoUrl} type="video/mp4" />
              </video>
            ) : thumb ? (
              <img
                data-testid="result-image"
                src={thumb}
                alt={ad.title || "Ad image"}
                className="w-full aspect-video object-contain bg-white dark:bg-zinc-950"
              />
            ) : null}
          </div>
          )}

          {/* Actions sidebar */}
          <div className={`col-span-12 ${hasCarousel ? "" : "lg:col-span-4"} flex flex-col gap-4`}>
            <div className="border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-6">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-500 mb-2">
                Advertiser
              </p>
              <h3
                data-testid="result-page-name"
                className="font-display font-bold text-zinc-900 dark:text-white text-xl leading-tight"
              >
                {ad.page_name || "Unknown page"}
              </h3>
              {ad.ad_id && (
                <p className="mt-2 text-xs font-mono text-zinc-500 dark:text-zinc-500 flex items-center gap-2">
                  ID: {ad.ad_id}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(ad.ad_id);
                      toast.success("Ad ID copied");
                    }}
                    data-testid="copy-ad-id-btn"
                    className="hover:text-yellow-500 transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </p>
              )}
              {ad.body && (
                <div className="mt-4 border-t border-zinc-200 dark:border-zinc-800 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-500">
                      Ad copy
                    </p>
                    <button
                      onClick={copyBody}
                      data-testid="copy-ad-body-btn"
                      className="inline-flex items-center gap-1.5 text-xs text-zinc-400 dark:text-zinc-600 dark:text-zinc-400 hover:text-yellow-500 border border-zinc-200 dark:border-zinc-800 hover:border-yellow-500 px-2 py-1 transition-colors"
                    >
                      {copied ? (
                        <>
                          <CheckCheck className="w-3 h-3 text-green-400" />
                          <span className="text-green-400">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          Copy text
                        </>
                      )}
                    </button>
                  </div>
                  <p
                    data-testid="result-body"
                    className="text-sm text-zinc-400 dark:text-zinc-600 dark:text-zinc-400 leading-relaxed line-clamp-6 whitespace-pre-line"
                  >
                    {ad.body}
                  </p>
                </div>
              )}
            </div>

            {ad.video_hd_url && (
              <button
                data-testid="download-video-hd-btn"
                onClick={() => {
                  triggerDownload(ad.video_hd_url, `${base}_hd`);
                  toast.success("HD download started");
                }}
                className="group flex items-center justify-between gap-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-5 h-14 transition-all hover:-translate-y-0.5"
              >
                <span className="flex items-center gap-3">
                  <Video className="w-5 h-5" strokeWidth={2.5} />
                  Download video · HD
                </span>
                <Download className="w-5 h-5 group-hover:translate-y-0.5 transition-transform" />
              </button>
            )}

            {ad.video_sd_url && !ad.video_hd_url && (
              <button
                data-testid="download-video-sd-btn"
                onClick={() => {
                  triggerDownload(ad.video_sd_url, `${base}_sd`);
                  toast.success("Download started");
                }}
                className="group flex items-center justify-between gap-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-5 h-14 transition-all hover:-translate-y-0.5"
              >
                <span className="flex items-center gap-3">
                  <Film className="w-5 h-5" strokeWidth={2.5} />
                  Download video · SD
                </span>
                <Download className="w-5 h-5 group-hover:translate-y-0.5 transition-transform" />
              </button>
            )}

            {ad.video_sd_url && ad.video_hd_url && (
              <button
                data-testid="download-video-sd-btn"
                onClick={() => {
                  triggerDownload(ad.video_sd_url, `${base}_sd`);
                  toast.success("SD download started");
                }}
                className="group flex items-center justify-between gap-3 border border-zinc-200 dark:border-zinc-800 hover:border-yellow-500 bg-transparent text-zinc-900 dark:text-white font-semibold px-5 h-14 transition-all hover:-translate-y-0.5"
              >
                <span className="flex items-center gap-3">
                  <Film className="w-5 h-5" strokeWidth={2} />
                  Download video · SD
                </span>
                <Download className="w-5 h-5" />
              </button>
            )}

            {(ad.image_url || ad.preview_image_url) && (
              <button
                data-testid="download-image-btn"
                onClick={() => {
                  triggerDownload(
                    ad.image_url || ad.preview_image_url,
                    `${base}_thumb`
                  );
                  toast.success("Image download started");
                }}
                className="group flex items-center justify-between gap-3 border border-zinc-200 dark:border-zinc-800 hover:border-yellow-500 bg-transparent text-zinc-900 dark:text-white font-semibold px-5 h-14 transition-all hover:-translate-y-0.5"
              >
                <span className="flex items-center gap-3">
                  <ImageIcon className="w-5 h-5" strokeWidth={2} />
                  Download thumbnail
                </span>
                <Download className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <CompetitorIntelligence ad={ad} />
        <AdAnalysis ad={ad} />
      </div>
    </section>
  );
}
